import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import { Icon } from "../components/ui";
import { dumpDiagnostics } from "../core/diagnosticsBuffer";
import { useDictation } from "../dictation";
import useLiveTranscript from "./useLiveTranscript";
import "./beta-error-button.scss";

/**
 * The beta tools button — a short draggable bar every store sees when the
 * master-admin "Beta Error Reporting" toggle is on. Hovering (or tapping, on
 * touch) REPLACES the bar with two actions; moving away puts the bar back:
 *  - "Report bug" (camera) — captures a screenshot (html2canvas, lazy-loaded),
 *    bundles the rolling console / failed-request buffer with the timestamp,
 *    and files it for the tech team.
 *  - "Press & Dictate" (mic) — the same report plus a spoken note of up to
 *    two minutes. The browser's speech-to-text streams the words live (purple);
 *    the recording itself (the listing-dictation recorder) is sent with the
 *    report on VALIDATE and Gemini transcribes it server-side. CANCEL discards
 *    everything — no audio, transcript, screenshot or logs leave the browser.
 *
 * Sits bottom-right ABOVE the "Set up your store" onboarding widget by
 * default; drag it anywhere — the position is remembered per browser. Names
 * and the live text open on the side facing the middle of the screen.
 */

const POSITION_KEY = "rc.betaErrorButton.pos";
const DRAG_THRESHOLD_PX = 5;
// The bar's hit area — larger than the bar itself so it is easy to grab.
const HANDLE_W = 24;
const HANDLE_H = 44;
// Closer than this to the top edge, the panel opens downwards.
const FLIP_TOP_PX = 180;
// Less room than this under the handle, the live text goes above the panel.
// The default anchor leaves ~88px, and the text box is capped to fit in it.
const TEXT_ROOM_PX = 84;
const SENT_HOLD_MS = 2500;
// Spoken notes run to two minutes, warned 15s before. Recorded at 8kHz so two
// minutes of WAV (~1.9MB) stays inside the backend's 2MB audio cap and the 5MB
// request limit alongside the screenshot — 16kHz would double it.
const DICTATION_MAX_MS = 120_000;
const DICTATION_WARN_MS = 105_000;
const DICTATION_SAMPLE_RATE = 8000;
const WAVE_PROFILE = [0.35, 0.55, 0.75, 0.9, 1, 0.9, 0.75, 0.55, 0.35];

const clampToViewport = (x, y) => ({
  x: Math.min(Math.max(x, 0), window.innerWidth - HANDLE_W),
  y: Math.min(Math.max(y, 0), window.innerHeight - HANDLE_H),
});

const loadPosition = () => {
  try {
    const raw = localStorage.getItem(POSITION_KEY);
    if (!raw) return null;
    const pos = JSON.parse(raw);
    if (typeof pos?.x !== "number" || typeof pos?.y !== "number") return null;
    // Keep it on-screen even if the viewport shrank since it was saved.
    return clampToViewport(pos.x, pos.y);
  } catch {
    return null;
  }
};

const savePosition = (pos) => {
  try {
    localStorage.setItem(POSITION_KEY, JSON.stringify(pos));
  } catch {
    // storage may be unavailable — the button just resets next load
  }
};

/** Which way the panel, its names and the live text should open from here. */
const measureLayout = (el) => {
  if (!el) return { leftHalf: false, below: false, textAbove: false };
  const rect = el.getBoundingClientRect();
  const below = rect.top < FLIP_TOP_PX;
  return {
    leftHalf: rect.left + rect.width / 2 < window.innerWidth / 2,
    below,
    textAbove: !below && window.innerHeight - rect.bottom < TEXT_ROOM_PX,
  };
};

export const useBetaReportingEnabled = (hasUser) =>
  useQuery({
    queryKey: ["beta-reporting", "config"],
    enabled: Boolean(hasUser),
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
    queryFn: async () => {
      const { data } = await axios.get("/api/beta-reporting/config", {
        metadata: { silent: true },
      });
      return Boolean(data?.enabled);
    },
  });

const REPORT_LABEL = {
  idle: "Report bug",
  busy: "Sending…",
  sent: "Sent — thank you",
  failed: "Failed — try again",
};

const REPORT_ICON = {
  idle: "Camera",
  busy: "Loader2",
  sent: "CheckCircle2",
  failed: "AlertTriangle",
};

const Wave = ({ level, active }) => {
  // Peak amplitude of normal speech sits around 0.1–0.4; scale it to the bar.
  const gain = active ? Math.min(1, level * 3) : 0;
  return (
    <div className="rc-beta-fab__wave" aria-hidden="true">
      {WAVE_PROFILE.map((weight, i) => (
        <span
          key={i}
          className="rc-beta-fab__wave-bar"
          // The jitter is what makes it "vibrate" with the voice, not just swell.
          style={{ width: 3 + gain * weight * (0.65 + Math.random() * 0.35) * 15 }}
        />
      ))}
    </div>
  );
};

const BetaErrorButton = () => {
  const [pos, setPos] = useState(null); // null = default CSS anchor
  const [mode, setMode] = useState("closed"); // closed | menu | dictate
  const [layout, setLayout] = useState(() => measureLayout(null));
  const [reportState, setReportState] = useState("idle"); // idle | busy | sent | failed
  const [sendState, setSendState] = useState("idle"); // dictation: idle | sending | sent | failed
  const rootRef = useRef(null);
  const textRef = useRef(null);
  const dragRef = useRef(null);
  const hoveringRef = useRef(false);
  // Set by a completed drag so the synthetic click that follows is ignored.
  const suppressClickRef = useRef(false);
  const blobRef = useRef(null);

  // The recorder hands us the WAV instead of uploading it: the report decides
  // when it is sent (VALIDATE), and the 2-minute auto-stop just parks it here.
  const upload = useCallback(async (blob) => {
    blobRef.current = blob;
    return true;
  }, []);
  const dictation = useDictation({
    upload,
    maxMs: DICTATION_MAX_MS,
    warnAtMs: DICTATION_WARN_MS,
    sampleRate: DICTATION_SAMPLE_RATE,
  });
  const { cancel: cancelRecording, stop: stopRecording, start: startRecording } = dictation;
  // Pulled apart so effects depend on the stable callbacks, not a fresh object per render.
  const { text: liveText, unavailable: liveUnavailable, start: startLive, stop: stopLive } = useLiveTranscript();

  useEffect(() => {
    setPos(loadPosition());
    // A spot saved on a wide window is off-screen on a narrow one (desktop →
    // phone view, rotating a tablet): pull it back in whenever the viewport
    // changes. Display only — the saved spot is left alone.
    const onResize = () => setPos((current) => (current ? clampToViewport(current.x, current.y) : current));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const open = (next) => {
    setLayout(measureLayout(rootRef.current));
    setMode(next);
  };

  useEffect(() => {
    if (reportState === "sent" || reportState === "failed") {
      const timer = setTimeout(() => {
        setReportState("idle");
        if (!hoveringRef.current) setMode((m) => (m === "menu" ? "closed" : m));
      }, 4000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [reportState]);

  // Touch has no hover-out: a tap anywhere else collapses the menu (never a dictation).
  useEffect(() => {
    if (mode !== "menu") return undefined;
    const onOutside = (event) => {
      if (!rootRef.current?.contains(event.target) && reportState === "idle") setMode("closed");
    };
    document.addEventListener("pointerdown", onOutside);
    return () => document.removeEventListener("pointerdown", onOutside);
  }, [mode, reportState]);

  // Recording ended (2-minute limit or VALIDATE): the live preview stops with it.
  const { phase } = dictation;
  useEffect(() => {
    if (mode === "dictate" && !["idle", "requesting-permission", "recording"].includes(phase)) stopLive();
  }, [mode, phase, stopLive]);

  // Keep the newest words in view.
  useEffect(() => {
    if (textRef.current) textRef.current.scrollTop = textRef.current.scrollHeight;
  }, [liveText]);

  const resetDictation = useCallback(() => {
    stopLive();
    cancelRecording();
    blobRef.current = null;
    setSendState("idle");
    setMode(hoveringRef.current ? "menu" : "closed");
  }, [stopLive, cancelRecording]);

  useEffect(() => {
    if (sendState !== "sent") return undefined;
    const timer = setTimeout(resetDictation, SENT_HOLD_MS);
    return () => clearTimeout(timer);
  }, [sendState, resetDictation]);

  // ── Dragging (from the bar or anywhere on the panel) ─────────────────────

  const onDragMove = (event) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (!drag.moved
      && Math.abs(event.clientX - drag.startX) < DRAG_THRESHOLD_PX
      && Math.abs(event.clientY - drag.startY) < DRAG_THRESHOLD_PX) {
      return;
    }
    drag.moved = true;
    setPos(clampToViewport(event.clientX - drag.offsetX, event.clientY - drag.offsetY));
  };

  const onDragEnd = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (drag?.moved) {
      // A drag is not a click — persist and swallow the click that follows.
      setPos((current) => {
        if (current) savePosition(current);
        return current;
      });
      suppressClickRef.current = true;
      setLayout(measureLayout(rootRef.current));
    }
  };

  // Move/up are followed on the WINDOW, not the widget: a quick flick leaves
  // the widget before its first move event, and pointer capture is not an
  // option — it retargets the click, so a plain tap would stop reaching the
  // button under it.
  const onPointerDown = (event) => {
    if (event.button !== 0) return;
    suppressClickRef.current = false;
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      moved: false,
    };
    const end = () => {
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      onDragEnd();
    };
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  };

  const onClickCapture = (event) => {
    if (!suppressClickRef.current) return;
    suppressClickRef.current = false;
    event.stopPropagation();
    event.preventDefault();
  };

  // ── Hover in / out (mouse only — a touch tap fires mouse events too) ──────

  const onPointerEnter = (event) => {
    if (event.pointerType !== "mouse") return;
    hoveringRef.current = true;
    if (mode === "closed") open("menu");
  };

  const onPointerLeave = (event) => {
    if (event.pointerType !== "mouse") return;
    hoveringRef.current = false;
    if (mode === "menu" && reportState === "idle" && !dragRef.current) setMode("closed");
  };

  // ── Filing a report ──────────────────────────────────────────────────────

  /** Screenshot + logs (+ dictation), the same bundle for both actions. Throws on failure. */
  const fileReport = async ({ audio = null, liveTranscript = "" } = {}) => {
    // Snapshot the diagnostics FIRST — the capture below logs its own noise.
    const logs = dumpDiagnostics();
    const clientTime = new Date().toISOString();

    let screenshot = null;
    try {
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        scale: 1,
        // What the person is LOOKING AT, not the whole document. A full-page
        // capture of a long page (Trade Feed: 36,000px) passed Chrome's
        // 32,767px canvas limit and came back as garbage — dark band, white
        // void, stray shapes — at ~7MB, over the 5MB upload cap. The visible
        // window is ~1280x760 and ~170KB, with sticky/fixed UI in place.
        x: window.scrollX,
        y: window.scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        // Leave the widget out of the picture rather than hiding it — hiding
        // made the sending spinner blink out for the length of the capture.
        ignoreElements: (node) => node === rootRef.current,
      });
      screenshot = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    } catch {
      // No screenshot is still a useful report.
    }

    const form = new FormData();
    if (screenshot) form.append("screenshot", screenshot, "screenshot.png");
    form.append("logs", JSON.stringify(logs));
    form.append("url", window.location.href.slice(0, 1024));
    form.append("client_time", clientTime);
    if (audio) form.append("audio", audio, "dictation.wav");
    if (liveTranscript.trim()) form.append("live_transcript", liveTranscript.trim().slice(0, 5000));

    await axios.post("/api/beta-reports", form, {
      headers: { "Content-Type": "multipart/form-data" },
      metadata: { silent: true },
    });
  };

  const reportBug = async () => {
    if (reportState === "busy") return;
    setReportState("busy");
    try {
      await fileReport();
      setReportState("sent");
    } catch {
      setReportState("failed");
    }
  };

  // ── Dictation ────────────────────────────────────────────────────────────

  const startDictation = () => {
    blobRef.current = null;
    setSendState("idle");
    open("dictate");
    // Speech recognition first, inside the tap — iOS only allows it from a gesture.
    startLive();
    startRecording();
  };

  const validate = async () => {
    if (sendState === "sending") return;
    setSendState("sending");
    const transcript = liveText;
    stopLive();
    // Still recording: stop() encodes and parks the WAV via `upload` above.
    // A silent recording leaves no blob, and the report goes without audio.
    if (phase === "recording") await stopRecording();
    try {
      await fileReport({ audio: blobRef.current, liveTranscript: transcript });
      setSendState("sent");
    } catch {
      setSendState("failed");
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const recording = phase === "recording";
  const sending = sendState === "sending";

  let note = null;
  let noteKind = "transcript";
  if (sendState === "sent") {
    note = "Sent — thank you";
    noteKind = "status";
  } else if (sendState === "failed") {
    note = "Couldn't send — VALIDATE to try again.";
    noteKind = "error";
  } else if (phase === "error" && dictation.errorKind !== "silence") {
    note = dictation.errorMessage;
    noteKind = "error";
  } else if (phase === "requesting-permission") {
    note = "Allow the microphone to start…";
    noteKind = "status";
  } else if (liveText) {
    note = liveText;
  } else if (mode === "dictate" && !sending) {
    note = liveUnavailable
      ? "Listening… live text isn't available in this browser — your note is transcribed when you validate."
      : "Listening…";
    noteKind = "status";
  }
  const limitNote = phase === "review" && (sendState === "idle" || sendState === "failed")
    ? "2-minute limit reached — VALIDATE to send."
    : dictation.nearingLimit
      ? `${Math.ceil(dictation.remainingMs / 1000)}s left`
      : null;

  const classes = [
    "rc-beta-fab",
    `rc-beta-fab--${mode}`,
    layout.leftHalf && "rc-beta-fab--left-half",
    layout.below && "rc-beta-fab--below",
    layout.textAbove && "rc-beta-fab--text-above",
  ].filter(Boolean).join(" ");
  const style = pos ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" } : undefined;

  // Portalled to <body>, not left inside the app shell: modals and flows are
  // portalled there too, and a z-index only competes inside its own stacking
  // context — so from inside the shell the bar disappeared under every pop-up.
  return createPortal(
    <div
      ref={rootRef}
      className={classes}
      style={style}
      onPointerDown={onPointerDown}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onClickCapture={onClickCapture}
      onKeyDown={(event) => event.key === "Escape" && mode === "menu" && setMode("closed")}
    >
      {mode === "closed" && (
        <button
          type="button"
          className="rc-beta-fab__handle"
          aria-label="Beta tools — report a bug or dictate a note. Drag to move."
          aria-expanded={false}
          onClick={() => open("menu")}
        >
          <span className="rc-beta-fab__bar" />
        </button>
      )}

      {mode === "menu" && (
        <div className="rc-beta-fab__pill" role="group" aria-label="Beta tools">
          <button type="button" className="rc-beta-fab__action" aria-label="Press & Dictate" onClick={startDictation}>
            <Icon name="Mic" size="sm" />
            <span className="rc-beta-fab__tip" aria-hidden="true">Press &amp; Dictate</span>
          </button>
          <button
            type="button"
            className={`rc-beta-fab__action rc-beta-fab__action--${reportState}`}
            aria-label="Report bug — sends a screenshot, timestamp and logs to the tech team"
            onClick={reportBug}
          >
            <Icon name={REPORT_ICON[reportState]} size="sm" className={reportState === "busy" ? "rc-beta-fab__spin" : undefined} />
            <span className="rc-beta-fab__tip" aria-live="polite">{REPORT_LABEL[reportState]}</span>
          </button>
        </div>
      )}

      {mode === "dictate" && (
        <div className="rc-beta-fab__pill rc-beta-fab__pill--dictate" role="group" aria-label="Dictation">
          {!sending && sendState !== "sent" && (
            <button type="button" className="rc-beta-fab__round rc-beta-fab__round--cancel" aria-label="Cancel — discard the note, send nothing" onClick={resetDictation}>
              <Icon name="X" size="sm" />
              <span className="rc-beta-fab__tip" aria-hidden="true">CANCEL</span>
            </button>
          )}
          <Wave level={dictation.level} active={recording} />
          {sending ? (
            <span className="rc-beta-fab__round rc-beta-fab__round--busy" role="status" aria-label="Sending">
              <Icon name="Loader2" size="sm" className="rc-beta-fab__spin" />
            </span>
          ) : (
            <button
              type="button"
              className="rc-beta-fab__round rc-beta-fab__round--validate"
              aria-label="Validate — send the note with a screenshot and logs"
              disabled={sendState === "sent" || phase === "requesting-permission"}
              onClick={validate}
            >
              <Icon name="Check" size="sm" />
              <span className="rc-beta-fab__tip" aria-hidden="true">VALIDATE</span>
            </button>
          )}

          {(note || limitNote) && (
            <div ref={textRef} className={`rc-beta-fab__note rc-beta-fab__note--${noteKind}`} aria-live="polite">
              {note}
              {limitNote && <span className="rc-beta-fab__limit">{limitNote}</span>}
            </div>
          )}
        </div>
      )}
    </div>,
    document.body
  );
};

export default BetaErrorButton;
